/**
 * Resolve a bundle product's selling plan for a given quantity BY NAME.
 *
 * The old code used sellingPlans[qty + 2]. Shopify orders selling plans by their
 * selling-plan-GROUP position (NOT by plan id -- verified 2026-09-18 against live data:
 * butyric-acid's allocation ids are not ascending). So the "Bundle:" tier only sits at
 * 3..5 on products whose Bundle groups happen to be positioned after exactly three other
 * groups. sibo-cleanse-tonic had no Bundle plans at all, so [qty+2] resolved to leftover
 * "Tier Offer" A/B plans (and at qty 3 to undefined, falling back to a monthly plan).
 *
 * Its three Bundle plans were created in Recharge on 2026-09-18 and landed at group
 * positions 7/8/9 -> allocation indices 5/6/7, which is exactly why the index approach
 * still would not have found them. Name matching is stable regardless of group position.
 * Falls back to the original index behaviour when no Bundle plan exists, so nothing can
 * regress on a product that lacks them.
 *
 * Verified equivalent: across all 6 protocol shells x every child x qty 1/2/3 (51 checks),
 * name matching resolves the SAME selling plan id the old index code did.
 */
function resolveBundlePlan(sellingPlans, quantity) {
    if (!sellingPlans || !sellingPlans.length) return null;
    const want = quantity === 1 ? 'bundle: delivery every month'
               : quantity === 2 ? 'bundle: delivery every 2 months'
               : 'bundle: delivery every 3 months';
    const byName = sellingPlans.find(p => p && typeof p.name === 'string'
        && p.name.trim().toLowerCase() === want);
    if (byName) return byName;
    return sellingPlans[quantity + 2] || sellingPlans[0];
}

/**
 * Bundle Purchase Handler
 * Manages multi-product bundles using metafield product references
 * Adds all products to cart together with quantity multiplier
 */

class BundlePurchase {
  constructor() {
    this.container = document.querySelector('.bulk-purchase-v2');

    if (!this.container) {
      return;
    }

    this.bundleData = this.loadBundleData();
    this.selectedQuantity = 1;

    // Read metafield for default purchase type
    const defaultSubscription = this.container.getAttribute('data-default-subscription') === 'true';
    this.purchaseType = defaultSubscription ? 'subscription' : 'onetime';

    // Get main product title from page - use h1 within .product__title to avoid subheadings
    const titleElement = document.querySelector('.product__title h1');
    if (titleElement) {
      // Use innerText to get just the h1 text without nested elements
      this.mainProductTitle = (titleElement.innerText || titleElement.textContent).trim();
    } else {
      this.mainProductTitle = 'Bundle';
    }

    this.init();
  }

  loadBundleData() {
    const scriptTag = document.querySelector('script[id^="bundle-data-"]');
    if (!scriptTag) {
      console.error('Bundle data script tag not found');
      return null;
    }

    try {
      const data = JSON.parse(scriptTag.textContent);
      return data;
    } catch (error) {
      console.error('Failed to parse bundle data:', error);
      return null;
    }
  }

  init() {
    this.bindQuantityButtons();
    this.bindPurchaseTypeButtons();
    this.bindAddToCart();
    this.initializeCardStates();
    this.updatePriceDisplay();
    this.updateDiscountBadges();
    this.updateSubscriptionBenefitsText();
  }

  initializeCardStates() {
    // Set initial quantity card state (1 bottle of each selected)
    const quantityCards = this.container.querySelectorAll('.quantity-card');
    quantityCards.forEach(card => {
      const radio = card.querySelector('.quantity-radio');
      const cardInner = card.querySelector('.quantity-card-inner');

      if (parseInt(radio.dataset.quantity) === 1) {
        radio.checked = true;
        cardInner.classList.add('border-black', 'shadow-lg', 'bg-white');
        cardInner.classList.remove('border-gray-300', 'bg-transparent');
      } else {
        radio.checked = false;
        cardInner.classList.remove('border-black', 'shadow-lg', 'bg-white');
        cardInner.classList.add('border-gray-300', 'bg-transparent');
      }
    });

    // Set initial purchase type state (respects metafield default)
    const purchaseCards = this.container.querySelectorAll('.purchase-card');
    purchaseCards.forEach(card => {
      const input = card.querySelector('.purchase-type-input');
      const cardInner = card.querySelector('.purchase-card-inner');
      const radioButton = card.querySelector('.radio-button');
      const checkmarkIcon = card.querySelector('.checkmark-icon');

      if (input.dataset.type === this.purchaseType) {
        input.checked = true;
        cardInner.classList.add('border-black', 'shadow-lg', 'bg-white');
        cardInner.classList.remove('border-gray-300', 'bg-transparent');
        if (radioButton) {
          radioButton.classList.add('bg-[#5F7C78]', 'border-[#5F7C78]');
          radioButton.classList.remove('border-gray-300', 'bg-transparent');
        }
        if (checkmarkIcon) checkmarkIcon.classList.remove('hidden');
      } else {
        input.checked = false;
        cardInner.classList.remove('border-black', 'shadow-lg', 'bg-white');
        cardInner.classList.add('border-gray-300', 'bg-transparent');
        if (radioButton) {
          radioButton.classList.remove('bg-[#5F7C78]', 'border-[#5F7C78]');
          radioButton.classList.add('border-gray-300', 'bg-transparent');
        }
        if (checkmarkIcon) checkmarkIcon.classList.add('hidden');
      }
    });
  }

  bindQuantityButtons() {
    const quantityRadios = this.container.querySelectorAll('.quantity-radio');

    quantityRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        if (radio.checked) {
          const quantity = parseInt(radio.dataset.quantity);
          this.selectQuantityOption(quantity);
        }
      });
    });
  }

  bindPurchaseTypeButtons() {
    const purchaseInputs = this.container.querySelectorAll('.purchase-type-input');

    purchaseInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        if (input.checked) {
          const type = input.dataset.type;
          this.updatePurchaseType(type);
        }
      });
    });
  }

  bindAddToCart() {
    const addToCartBtn = this.container.querySelector('.bundle-add-to-cart');
    if (!addToCartBtn) return;

    addToCartBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      await this.addToCart();
    });
  }

  selectQuantityOption(quantity) {
    this.selectedQuantity = quantity;

    /* BEGIN: PostHog Bundle Quantity Selected tracking (added 2026-02-24) */
    if (typeof posthog !== 'undefined') {
      posthog.capture('Bundle Quantity Selected', {
        bottle_count: quantity,
        purchase_type: this.purchaseType,
        bundle_title: this.mainProductTitle,
        product_count: this.bundleData?.products?.length || 0,
        source: 'bundle_form'
      });
    }
    /* END: PostHog Bundle Quantity Selected tracking */

    // Update radio buttons and card styling
    const quantityCards = this.container.querySelectorAll('.quantity-card');
    quantityCards.forEach(card => {
      const radio = card.querySelector('.quantity-radio');
      const cardInner = card.querySelector('.quantity-card-inner');

      if (parseInt(radio.dataset.quantity) === quantity) {
        radio.checked = true;
        cardInner.classList.add('border-black', 'shadow-lg', 'bg-white');
        cardInner.classList.remove('border-gray-300', 'bg-transparent');
      } else {
        radio.checked = false;
        cardInner.classList.remove('border-black', 'shadow-lg', 'bg-white');
        cardInner.classList.add('border-gray-300', 'bg-transparent');
      }
    });

    // Update delivery frequency text
    this.updateDeliveryFrequency();

    // Update price display
    this.updatePriceDisplay();

    // Update subscription benefits text
    this.updateSubscriptionBenefitsText();
  }

  updatePurchaseType(type) {
    this.purchaseType = type;

    /* BEGIN: PostHog Bundle Purchase Type Changed tracking (added 2026-02-24) */
    if (typeof posthog !== 'undefined') {
      posthog.capture('Purchase Type Changed', {
        purchase_type: type,
        bottle_count: this.selectedQuantity,
        bundle_title: this.mainProductTitle,
        product_count: this.bundleData?.products?.length || 0,
        source: 'bundle_form'
      });
    }
    /* END: PostHog Bundle Purchase Type Changed tracking */

    const purchaseCards = this.container.querySelectorAll('.purchase-card');

    purchaseCards.forEach(card => {
      const input = card.querySelector('.purchase-type-input');
      const cardInner = card.querySelector('.purchase-card-inner');
      const radioButton = card.querySelector('.radio-button');
      const checkmarkIcon = card.querySelector('.checkmark-icon');

      if (input.dataset.type === type) {
        input.checked = true;
        cardInner.classList.add('border-black', 'shadow-lg', 'bg-white');
        cardInner.classList.remove('border-gray-300', 'bg-transparent');
        if (radioButton) {
          radioButton.classList.add('bg-[#5F7C78]', 'border-[#5F7C78]');
          radioButton.classList.remove('border-gray-300', 'bg-transparent');
        }
        if (checkmarkIcon) checkmarkIcon.classList.remove('hidden');
      } else {
        input.checked = false;
        cardInner.classList.remove('border-black', 'shadow-lg', 'bg-white');
        cardInner.classList.add('border-gray-300', 'bg-transparent');
        if (radioButton) {
          radioButton.classList.remove('bg-[#5F7C78]', 'border-[#5F7C78]');
          radioButton.classList.add('border-gray-300', 'bg-transparent');
        }
        if (checkmarkIcon) checkmarkIcon.classList.add('hidden');
      }
    });

    // Update delivery frequency text
    this.updateDeliveryFrequency();

    // Update price display
    this.updatePriceDisplay();

    // Update discount badges
    this.updateDiscountBadges();

    // Update subscription benefits text
    this.updateSubscriptionBenefitsText();
  }

  async addToCart() {
    if (!this.bundleData || !this.bundleData.products || this.bundleData.products.length === 0) {
      console.error('No bundle products to add');
      return;
    }

    const button = this.container.querySelector('.bundle-add-to-cart');
    const buttonText = button.querySelector('.button-text');
    const spinner = button.querySelector('.loading-spinner');

    // Show loading state
    button.disabled = true;
    buttonText.classList.add('hidden');
    spinner.classList.remove('hidden');

    try {
      // Generate unique bundle ID to group all products together
      const bundleId = `bundle_${Date.now()}`;

      // Add products sequentially to avoid race conditions
      let eguideAdded = false;
      for (const product of this.bundleData.products) {
        // For subscriptions, find the appropriate selling plan based on quantity
        // For one-time purchases, no selling plan
        let sellingPlanId = null;

        if (this.purchaseType === 'subscription' && product.sellingPlans && product.sellingPlans.length > 0) {
          // For bundles, use selling plans 3-5 (15%, 20%, 25%)
          // Plans 0-2 are for individual products (10%, 15%, 20%)
          // 1 bottle → plan index 3 (15% off)
          // 2 bottles → plan index 4 (20% off)
          // 3 bottles → plan index 5 (25% off)
          const selectedPlan = resolveBundlePlan(product.sellingPlans, this.selectedQuantity);

          sellingPlanId = selectedPlan.id;
        }

        // Only add e-guide properties to the first bundle item
        const includeEguide = !eguideAdded && this.bundleData.eguide;
        if (includeEguide) eguideAdded = true;

        try {
          await this.addProductToCart(product.variantId, this.selectedQuantity, sellingPlanId, bundleId, includeEguide);
        } catch (error) {
          console.error(`Failed to add ${product.title}:`, error);
          throw error; // Re-throw to stop the process
        }
      }

      /* BEGIN: PostHog Bundle Add to Cart tracking (added 2026-02-24) */
      if (typeof posthog !== 'undefined') {
        const bundleTotal = this.calculateBundleTotal();
        posthog.capture('Add to Cart', {
          bundle_title: this.mainProductTitle,
          bundle_id: bundleId,
          quantity: this.selectedQuantity,
          purchase_type: this.purchaseType,
          total_price: bundleTotal.total ? (bundleTotal.total / 100).toFixed(2) : undefined,
          product_count: this.bundleData.products.length,
          products: this.bundleData.products.map(p => ({
            product_id: p.id,
            title: p.title,
            variant_id: p.variantId,
            price: (p.price / 100).toFixed(2)
          })),
          currency: window.Shopify?.currency?.active || 'USD',
          source: 'bundle_form'
        });
      }
      /* END: PostHog Bundle Add to Cart tracking */

      // Success - trigger cart drawer/update
      this.handleCartSuccess();

    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      this.handleCartError(error);
    } finally {
      // Reset button state
      button.disabled = false;
      buttonText.classList.remove('hidden');
      spinner.classList.add('hidden');
    }
  }

  async addProductToCart(variantId, quantity, sellingPlanId = null, bundleId = null, includeEguide = false) {
    const data = {
      id: variantId,
      quantity: quantity
    };

    // Add selling plan for subscriptions
    if (sellingPlanId) {
      data.selling_plan = sellingPlanId;
    }

    // Add bundle properties to group items together
    if (bundleId) {
      data['properties[_bundle_id]'] = bundleId;
      data['properties[_bundle_type]'] = `${this.selectedQuantity}_bottles_each`;
      data['properties[_bundle_quantity]'] = this.selectedQuantity.toString();
      data['properties[_purchase_type]'] = this.purchaseType;
      data['properties[_hide_quantity]'] = 'true';
      data['properties[_bundle_title]'] = this.mainProductTitle;
      data['properties[_bundle_url]'] = window.location.pathname;
      data['properties[Protocol]'] = this.mainProductTitle;

      // Add delivery schedule for subscriptions
      if (this.purchaseType === 'subscription') {
        let deliverySchedule;
        if (this.selectedQuantity === 1) {
          deliverySchedule = 'Delivery every month';
        } else if (this.selectedQuantity === 2) {
          deliverySchedule = 'Delivery every 2 months';
        } else if (this.selectedQuantity === 3) {
          deliverySchedule = 'Delivery every 3 months';
        }
        if (deliverySchedule) {
          data['properties[_delivery_frequency]'] = deliverySchedule; // hidden: the plan name already shows in checkout
        }
      }

      // Phase 2 cart-upsell flags propagated from the shell product onto every child line,
      // so the cart drawer's subscribe gate can read them (shell isn't in the cart).
      if (this.bundleData && this.bundleData.cartFlags) {
        data['properties[_cart_subscribe_upsell]'] = String(this.bundleData.cartFlags.subscribe);
        data['properties[_cart_bulk_upsell]'] = String(this.bundleData.cartFlags.bulk);
        data['properties[_cart_exclude_sub_upsell]'] = String(this.bundleData.cartFlags.exclude);
      }
    }

    // Add e-guide properties (only on first bundle item to avoid duplicate events)
    if (includeEguide && this.bundleData.eguide) {
      const eguide = this.bundleData.eguide;
      data['properties[_eguide_headline]'] = eguide.headline || eguide.title || '';
      if (eguide.copy) {
        data['properties[_eguide_copy]'] = eguide.copy;
      }
      const eguideUrl = typeof eguide.url === 'object' ? (eguide.url.value || eguide.url.url || '') : eguide.url;
      data['properties[_eguide_url]'] = eguideUrl;
      if (eguide.image) {
        data['properties[_eguide_image]'] = eguide.image;
      }
    }

    const params = new URLSearchParams();
    for (const key in data) {
      params.append(key, data[key]);
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString()
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Cart API error:', errorText);
      throw new Error(`Failed to add product to cart: ${response.status} - ${errorText}`);
    }

    return await response.json();
  }

  calculateBundleTotal() {
    if (!this.bundleData || !this.bundleData.products) {
      return { total: 0, compareAtTotal: 0 };
    }

    let total = 0;
    let compareAtTotal = 0;

    this.bundleData.products.forEach(product => {
      if (this.purchaseType === 'subscription' && product.sellingPlans && product.sellingPlans.length > 0) {
        // For bundles, use selling plans 3-5 (15%, 20%, 25%)
        // Plans 0-2 are for individual products (10%, 15%, 20%)
        // 1 bottle → plan index 3 (15% off)
        // 2 bottles → plan index 4 (20% off)
        // 3 bottles → plan index 5 (25% off)
        const planToUse = resolveBundlePlan(product.sellingPlans, this.selectedQuantity);

        total += planToUse.price * this.selectedQuantity;
        compareAtTotal += product.price * this.selectedQuantity;
      } else {
        // One-time purchase
        total += product.price * this.selectedQuantity;
        if (product.compareAtPrice) {
          compareAtTotal += product.compareAtPrice * this.selectedQuantity;
        }
      }
    });

    return { total, compareAtTotal };
  }

  updatePriceDisplay() {
    // Calculate subscription price (always)
    const subscriptionTotal = this.calculatePriceForType('subscription');
    const onetimeTotal = this.calculatePriceForType('onetime');

    // Update subscription price
    const subscriptionPrice = this.container.querySelector('.subscription-price');
    const subscriptionComparePrice = this.container.querySelector('.subscription-compare-price');
    if (subscriptionPrice) {
      subscriptionPrice.textContent = this.formatMoney(subscriptionTotal);
    }
    if (subscriptionComparePrice) {
      // Show full price without any discount as compare price
      let fullPrice = 0;
      this.bundleData.products.forEach(product => {
        fullPrice += product.price * this.selectedQuantity;
      });
      subscriptionComparePrice.textContent = this.formatMoney(fullPrice);
    }

    // Update one-time price
    const onetimePrice = this.container.querySelector('.onetime-price');
    const onetimeComparePrice = this.container.querySelector('.onetime-compare-price');

    if (onetimePrice) {
      onetimePrice.textContent = this.formatMoney(onetimeTotal);
    }

    // Show compare price for one-time (original price before discount)
    if (onetimeComparePrice) {
      // Calculate full price without discount
      let fullPrice = 0;
      this.bundleData.products.forEach(product => {
        fullPrice += product.price * this.selectedQuantity;
      });
      onetimeComparePrice.textContent = this.formatMoney(fullPrice);
      onetimeComparePrice.classList.remove('hidden');
    }

    // Update inline price display (above subhead)
    const inlinePrice = document.querySelector('.pdp-inline-price');
    if (inlinePrice) {
      const displayPrice = this.purchaseType === 'subscription' ? subscriptionTotal : onetimeTotal;
      inlinePrice.textContent = this.formatMoney(displayPrice);
    }
  }

  calculatePriceForType(type) {
    if (!this.bundleData || !this.bundleData.products) {
      return 0;
    }

    let total = 0;

    this.bundleData.products.forEach(product => {
      if (type === 'subscription' && product.sellingPlans && product.sellingPlans.length > 0) {
        // For bundles, use selling plans 3-5 (15%, 20%, 25%)
        // Plans 0-2 are for individual products (10%, 15%, 20%)
        // 1 bottle → plan index 3 (15% off)
        // 2 bottles → plan index 4 (20% off)
        // 3 bottles → plan index 5 (25% off)
        const planToUse = resolveBundlePlan(product.sellingPlans, this.selectedQuantity);
        total += planToUse.price * this.selectedQuantity;
      } else {
        // One-time purchase - apply Shopify Function discounts
        // 1 bottle: 5% off, 2 bottles: 10% off, 3 bottles: 15% off
        let discountPercentage = 0;
        if (this.selectedQuantity === 1) {
          discountPercentage = 5;
        } else if (this.selectedQuantity === 2) {
          discountPercentage = 10;
        } else if (this.selectedQuantity === 3) {
          discountPercentage = 15;
        }

        const productTotal = product.price * this.selectedQuantity;
        // Round up per-product to match how Shopify calculates per line item
        const discountedPrice = Math.ceil(productTotal * (1 - discountPercentage / 100));
        total += discountedPrice;
      }
    });

    return total;
  }

  updateDeliveryFrequency() {
    const shippingFreq = this.container.querySelector('.shipping-frequency');
    if (!shippingFreq) {
      return;
    }

    let freqText;
    if (this.selectedQuantity === 1) {
      freqText = 'Delivery every month';
    } else if (this.selectedQuantity === 2) {
      freqText = 'Delivery every 2 months';
    } else if (this.selectedQuantity === 3) {
      freqText = 'Delivery every 3 months';
    } else {
      freqText = 'Delivery every month';
    }

    shippingFreq.textContent = freqText;
  }

  updateDiscountBadges() {
    // Show/hide discount badges based on purchase type
    const onetimeBadges = this.container.querySelectorAll('.onetime-discount-badge');
    const subscriptionBadges = this.container.querySelectorAll('.subscription-discount-badge');

    if (this.purchaseType === 'onetime') {
      // Show one-time badges, hide subscription badges
      onetimeBadges.forEach(badge => {
        badge.classList.remove('hidden');
      });
      subscriptionBadges.forEach(badge => {
        badge.classList.add('hidden');
      });
    } else {
      // Show subscription badges, hide one-time badges
      onetimeBadges.forEach(badge => {
        badge.classList.add('hidden');
      });
      subscriptionBadges.forEach(badge => {
        badge.classList.remove('hidden');
      });
    }
  }

  updateSubscriptionBenefitsText() {
    // Map quantity to first-order subscription discount: 1→10%, 2→15%, 3→20%
    const discountMap = { 1: 10, 2: 15, 3: 20 };
    const firstOrderDiscount = discountMap[this.selectedQuantity] || 10;
    const repeatDiscount = firstOrderDiscount + 5;

    // Update ALL instances on the page (main form + slider duplicate)
    document.querySelectorAll('.subscription-discount-text').forEach(el => {
      el.textContent = `Save ${firstOrderDiscount}% on every order`;
    });

    document.querySelectorAll('.subscription-repeat-text').forEach(el => {
      el.textContent = `Save ${repeatDiscount}% on your 2nd+ orders`;
    });
  }

  formatMoney(cents) {
    // Simple money formatting - adjust based on your currency settings
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  handleCartSuccess() {
    // Update cart icon bubble
    this.updateCartIconBubble();

    // Trigger cart drawer if available
    if (typeof fetchCartDrawer === 'function') {
      fetchCartDrawer();
    } else {
      // Fallback: redirect to cart
      window.location.href = '/cart';
    }
  }

  async updateCartIconBubble() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}?section_id=cart-icon-bubble`);
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const newCartIcon = html.querySelector('.shopify-section');

      if (newCartIcon) {
        const currentCartIcon = document.getElementById('cart-icon-bubble');
        if (currentCartIcon) {
          currentCartIcon.innerHTML = newCartIcon.innerHTML;
        }
      }
    } catch (error) {
      console.error('Failed to update cart icon:', error);
    }
  }

  handleCartError(error) {
    const button = this.container.querySelector('.bundle-add-to-cart');
    const originalText = button.querySelector('.button-text').textContent;

    button.querySelector('.button-text').textContent = 'Error - Please try again';

    setTimeout(() => {
      button.querySelector('.button-text').textContent = originalText;
    }, 3000);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new BundlePurchase();
  });
} else {
  new BundlePurchase();
}

